import React, {useEffect, useRef} from 'react'
import {funcOrValOr} from "./CommonJS";
import {classNameScrollbar, genOnKeys, MkSvg} from "./CommonHtml";
import {jsxTree, tailCheck, useDynamicPanels, useStateEx} from "./CommonReact";
import {Button, ButtonAF, FormLabel} from "./Inputs";
import {Card} from "./UIBlocks";
import {UIPopPanel} from "./UIPopPanel";
import Draggable from "react-draggable";

/**
 * useDialogs: facilitates opening an arbitrary modal panel.
 * Usage:
 *      in your root component:
 *          const {setDlg, dialogsJsx} = useDialogs();
 *      and return:
 *          {dialogsJsx}
 *      or if you don't want the whole component to refresh:
 *          const appCtx = useRef({}).current;
 *          <UseAsComponent use={useDialogs} ctrl={appCtx} toJsx={ctrl => ctrl.dialogsJsx}/>
 *      when opening dialog:
 *          const key = appCtx.setDlg({Elm: MyDialogComponent, params...})
 *          appCtx.closeDlg(key);           // optionally close the dialog from a callback.
 *      optionally parameters for setDlg:
 *          key: reuse the same dialog to replace with a new content.
 *          noOverlay
 *          absPos - by default true (which lets UIPopPanel set "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2").
 *                           can be set to false or a class string.
 *      in MyDialogComponent:
 function MyDialogComponent({onClose, params...}) {
                onClose.onClose = () => Boolean;      // option to get callback when panel about to close. return false to prevent.
                return (
                    <Dialog {...{onClose}} className="dlg-xl" title="Title"
                        main={<div className="flex flex-col gap-3"></div}
                        buttons={<>
                            <Button h="Cancel" className="px-6 py-2 text-base text-prm-main" onClick={onClose}/>
                            <Button h="Apply" className="px-10 py-2 text-base text-prm-contrast bg-blue-500" onClick={doApply} disabled={disableSave()}/>
                        </>}
                    />
                );
            }
 *
 * @return {{dialogsJsx: JSX, setDlg: (function({Elm: reactElement, (*)}): void)}}
 */
export const useDialogs = () => {
    const {setPanel: setDlg, panelsJsx: dialogsJsx, closePanel: closeDlg, isPanelOn} =
        useDynamicPanels(({Elm, panelId, hide, onClose, onHideDone, noOverlay, absPos, ...props}) =>
            <UIPopPanel
                key={panelId} transition show={!hide} {...{onClose, onHideDone}} absPos={absPos ?? true} classNameOverlay={!noOverlay && "bg-handle"}
                children={Elm && <Elm {...{onClose, ...props}}/>}
            />
        );

    function setMsgEx({msg, onOk, btnOk, ...props}) {
        const key = setDlg({Elm: MessageBox, msg, onOk: onOk && ((...args) => (closeDlg(key, true), onOk(...args))), btnOk, ...props});
        return key;
    }

    return {
        setDlg, dialogsJsx, setMsgEx, closeDlg, isPanelOn,
        setMsg: msg => setMsgEx({msg}),
    };
};


const clsDlgSize = {
    "2xs": "w-screen sm:w-[25rem]",
    xs: "w-screen sm:w-[30rem]",
    sm: "w-screen sm:w-[35rem]",
    lg: "w-screen sm:w-[85vw] lg:w-[60vw]",
    xl: "w-screen sm:w-[95vw] xl:w-[85vw] 2xl:w-[70vw]",
};

export function Dialog({onClose, title, icon, main, bottom, buttons, className, classNameTitle, classNameBtns, noDividers, noClose, mainProps, ...props}) {
    const rDrag = useRef();
    const vals = useRef({handle: "id"+Date.now()}).current;

    return (
        <Draggable nodeRef={rDrag} handle={"#"+vals.handle}>
            <div ref={rDrag} className="h-full flex">     {/*The only way to allow max-h- to hold actual pixel value for below h-full divs.*/}
                <div className={tailCheck(className,
                    "bg-bg2 rounded-lg px-8 flex flex-col overflow-hidden text-left shadow-2xl outline-none resize [&[style*=height]]:max-h-[unset]",
                    "max-h-[95vh]", "tall:max-h-[80vh]", clsDlgSize[className?.match(/dlg-([a-z0-9]+)/)?.[1] ?? "sm"])}
                     tabIndex={0} {...props}>     {/*Allow dialog to be focused (but hide outline), otherwise focus will go up to body.*/}
                    <div data-test="dialog-title" id={vals.handle}
                         className={`pt-8 flex-shrink-0 cursor-move flex items-center justify-between text-xl font-semibold ${classNameTitle}`}>
                        {title ?? ""}
                        {title && onClose &&
                            <Button className="self-start" data-test="dialog-close" h={<MkSvg className="h-6 w-6" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>} onClick={e => onClose()}/>}
                    </div>
                    {title && main && !noDividers && <div className="h-[2px] bg-bg1 my-5"/>}
                    {!main || typeof main === "boolean"? null :
                        <div data-test="dialog-main" className={`grow pr-2 relative overflow-auto border-bg3 ${classNameScrollbar}`} {...mainProps}>
                            {main}
                        </div>
                    }
                    {title && main && (bottom || buttons) && !noDividers && <div className="h-[2px] bg-bg1 mt-3"/>}
                    {(bottom || buttons) &&
                        <div className="min-h-[6.5rem] flex-shrink-0 flex items-center gap-x-4">
                            <div className="grow flex">
                                {bottom || null}
                            </div>
                            <div data-test="dialog-buttons" className={classNameBtns || "flex gap-3"}>
                                {buttons}
                            </div>
                        </div>}
                </div>
            </div>
        </Draggable>
    )
}

export function MessageBox({msg, title, btnOk, btnCancel, onOk, checkOk, onClose, btnOkType, buttons, autoFocus = true, ...props}) {
    return (
        <Dialog data-test="message-box"
            className={typeof msg === "string"? "whitespace-pre-wrap" : undefined}
            {...{onClose, title, ...props}}
            main={funcOrValOr(msg)}
            {...!buttons && genOnKeys({Enter: onOk || onClose, NumpadEnter: "Enter"})}
            buttons={buttons || <>
                {(btnOk || onOk || btnCancel) && btnCancel !== false &&
                    <Button
                        data-test="messagebox-cancel-button"
                        h={btnCancel || "Cancel"}
                        className="px-6 py-2 text-base text-prm-main"
                        onClick={onClose}
                    />}
                <ButtonAF
                    data-test="messagebox-ok-button"
                    h={btnOk || "Ok"}
                    disabled={btnOk && (checkOk || !onOk)}
                    className="px-10 py-2 text-base text-prm-contrast bg-prm-light"
                    onClick={onOk || onClose}
                    autoFocus={autoFocus}
                />
            </>}
        />
    );
}

// form?: [{label, cmpt, field, props}]
// dlg?: ({st, setState, rerender}) => jsx
// checkOk: st => return string if Ok not allowed
//
export function FormBox({form, formInit, dlg, onOk, checkOk, ...props}) {
    const {state: st, setState, onChange, rerender} = useStateEx(formInit);

    return <MessageBox
        {...props}
        msg={dlg? dlg({st, setState, rerender}) :
            <Card>
                {form?.map(({label, cmpt, field, classNameCol1, classNameCol2, props}) =>
                    jsxTree(
                        label && <FormLabel key={field} {...{field, label}}
                                            classNameCol1={classNameCol1 ?? "w-28"} classNameCol2={classNameCol2 ?? "flex items-center"}/>,
                        React.createElement(cmpt, {field, key: field, value: st[field], onChange, ...props})
                    )
                )}
            </Card>
        }
        checkOk={checkOk?.(st)}
        onOk={e => onOk?.(st)}
    />
}

export function TimedActionMessageBox({onClose, onOk, btnOk, durationSecs, ...props}) {
    const {state: st, rerender} = useStateEx({tm: Date.now()});
    useEffect(() => {
        const tmr = setInterval(rerender, 500);
        return () => {clearInterval(tmr)};
    }, []);

    const closeOk = () => (onClose(), onOk());
    const passed = (Date.now() - st.tm) / 1000 |0;
    if (passed >= durationSecs) setTimeout(closeOk);

    return <MessageBox {...{onClose, ...props}} btnOk={`${btnOk} (${durationSecs - passed})`} onOk={closeOk}/>;
}
